import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../shared/Dialog';
import { RiBankLine, RiStore2Line } from '@remixicon/react';
import { Money } from '@/lib/money';
import { ForSaleTicket, SoldTicket } from '@/lib/OrderApi';
import { MoneyValues } from '@/lib/TicketApi';
import { useLanguage } from '@/providers/LanguageProvider';

// Define the type for the ticket prop, expecting details needed for the modal
type SoldTicketWithDetails = SoldTicket & Partial<Pick<ForSaleTicket, 'listingPriceExFee' | 'resaleFeeAmountSeller'>> & { 
    sellerReceivable?: MoneyValues 
};

interface SoldDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: SoldTicketWithDetails | null; // Allow null initially
}

const SoldDetailsModal: React.FC<SoldDetailsModalProps> = ({ isOpen, onClose, ticket }) => {
  const { currentLanguage: lang } = useLanguage();

  if (!ticket) return null; // Don't render if ticket data isn't available

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
            <DialogTitle>Sold Ticket Details</DialogTitle>
            <DialogDescription>
                Details about your marketplace sale payout.
            </DialogDescription>
            <div className="space-y-2 py-2">
                {/* Calculation Breakdown with checks */}
                {ticket.listingPriceExFee && (
                <div className="flex justify-between text-sm">
                    <span>Sold Price (excl. fee):</span>
                    <span>{Money.fromInteger(ticket.listingPriceExFee.amount, ticket.listingPriceExFee.currency).display(lang)}</span>
                </div>
                )}
                {ticket.resaleFeeAmountSeller && (
                <div className="flex justify-between text-sm">
                    <span>Marketplace Fee (Seller):</span>
                    <span>- {Money.fromInteger(ticket.resaleFeeAmountSeller.amount, ticket.resaleFeeAmountSeller.currency).display(lang)}</span>
                </div>
                )}
                {ticket.sellerReceivable && (
                <div className="flex justify-between text-sm font-semibold pt-1 border-t border-dashed mt-2">
                    <span>You Receive:</span>
                    <span>{Money.fromInteger(ticket.sellerReceivable.amount, ticket.sellerReceivable.currency).display(lang)}</span>
                </div>
                )}
            </div>
            {ticket.externalMarketplace ? (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center text-sm text-blue-800">
                <RiStore2Line className="w-5 h-5 mr-2 shrink-0" />
                <span>Sold on {ticket.externalMarketplace}</span>
              </div>
            ) : (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center text-sm text-blue-800">
                <RiBankLine className="w-5 h-5 mr-2 shrink-0" />
                <span>Payout will be initiated to your connected bank account within 3 business days.</span>
              </div>
            )}
        </DialogContent>
    </Dialog>
  );
};

export default SoldDetailsModal; 